import { SortableTableHead } from '@/components/table/SortableTableHead';
import { useTableSort, type SortAccessors } from '@/hooks/useTableSort';
import {
  Badge,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  EmptyTableState,
  Loading,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@startupquickstart/frontend';
import { format } from 'date-fns';
import { Calendar, Clock, MoreVertical, Search, Shield, ShieldOff } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';

import { GuestListHeader } from './GuestListHeader';
import { getScanAccessState } from './guestAccessStatus';
import { useGuestAccessActions } from './useGuestAccessActions';
import { useHotelUserContext, type UserScanHistory } from './useHotelUserContext';

type Scan = UserScanHistory['scans'][number];
type ScanSortKey = 'scannedAt' | 'accessCode' | 'status' | 'expires';

/** Active access sorts before expired before revoked. */
const SCAN_STATE_RANK: Record<ReturnType<typeof getScanAccessState>, number> = {
  active: 0,
  expired: 1,
  revoked: 2,
};

const SCAN_ACCESSORS: SortAccessors<Scan, ScanSortKey> = {
  scannedAt: (s) => new Date(s.scannedAt).getTime(),
  accessCode: (s) => s.accessCode?.name.toLowerCase() ?? null,
  status: (s) => SCAN_STATE_RANK[getScanAccessState(s)],
  expires: (s) => (s.expiresAt ? new Date(s.expiresAt).getTime() : null),
};

function formatDateTime(dateString: string | null): string {
  if (!dateString) return '-';
  return format(new Date(dateString), 'MMM d, yyyy h:mm a');
}

export function HotelUserScansTab() {
  const { hotelId, userId } = useParams<{ hotelId: string; userId: string }>();
  const { userData, isLoading } = useHotelUserContext();
  const access = useGuestAccessActions({ hotelId, userId });
  const [search, setSearch] = useState('');

  const scans = useMemo(() => userData?.scans ?? [], [userData?.scans]);

  const filteredScans = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return scans;
    return scans.filter((scan) => scan.accessCode?.name.toLowerCase().includes(q));
  }, [scans, search]);

  const accessors = useMemo(() => SCAN_ACCESSORS, []);
  const { sort, toggleSort, sorted } = useTableSort<Scan, ScanSortKey>(filteredScans, accessors, {
    key: 'scannedAt',
    direction: 'desc',
  });

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Loading />
      </div>
    );
  }

  return (
    <>
      <GuestListHeader
        title="Scans"
        description="Manage this guest's access-code scans"
        search={
          scans.length
            ? { value: search, onChange: setSearch, placeholder: 'Search by access code...' }
            : undefined
        }
      />

      {scans.length === 0 ? (
        <div className="rounded-xl border bg-card shadow-sm py-12 px-6">
          <EmptyTableState
            icon={<Shield className="h-8 w-8" />}
            title="No scans yet"
            description="Scan history will appear here when the guest scans a hotel access code."
          />
        </div>
      ) : filteredScans.length === 0 ? (
        <div className="rounded-xl border bg-card shadow-sm py-12 px-6">
          <EmptyTableState
            icon={<Search className="h-8 w-8" />}
            title="No matching scans"
            description="No scans match your search."
          />
        </div>
      ) : (
        <div className="rounded-xl border bg-card shadow-sm overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <SortableTableHead
                  label="Scanned At"
                  sortKey="scannedAt"
                  activeKey={sort.key}
                  direction={sort.direction}
                  onSort={toggleSort}
                />
                <SortableTableHead
                  label="Access Code"
                  sortKey="accessCode"
                  activeKey={sort.key}
                  direction={sort.direction}
                  onSort={toggleSort}
                />
                <SortableTableHead
                  label="Status"
                  sortKey="status"
                  activeKey={sort.key}
                  direction={sort.direction}
                  onSort={toggleSort}
                />
                <SortableTableHead
                  label="Expires"
                  sortKey="expires"
                  activeKey={sort.key}
                  direction={sort.direction}
                  onSort={toggleSort}
                />
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sorted.map((scan) => {
                const state = getScanAccessState(scan);
                return (
                  <TableRow key={scan.id}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Clock className="h-4 w-4 text-muted-foreground" />
                        {formatDateTime(scan.scannedAt)}
                      </div>
                    </TableCell>
                    <TableCell>
                      {scan.accessCode ? (
                        <span className="font-mono text-sm">{scan.accessCode.name}</span>
                      ) : (
                        '-'
                      )}
                    </TableCell>
                    <TableCell>
                      {state === 'revoked' ? (
                        <Badge variant="destructive">
                          <ShieldOff className="h-3 w-3 mr-1" />
                          Revoked
                        </Badge>
                      ) : state === 'active' ? (
                        <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200">
                          <Shield className="h-3 w-3 mr-1" />
                          Active
                        </Badge>
                      ) : (
                        <Badge variant="secondary">
                          <ShieldOff className="h-3 w-3 mr-1" />
                          Expired
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Calendar className="h-4 w-4 text-muted-foreground" />
                        {formatDateTime(scan.expiresAt)}
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => access.openAdjust(scan)}>
                            Adjust Expiration
                          </DropdownMenuItem>
                          {state === 'active' && (
                            <>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem
                                className="text-destructive"
                                onClick={() => access.openRevoke(scan)}
                              >
                                Revoke Access
                              </DropdownMenuItem>
                            </>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}

      {access.dialogs}
    </>
  );
}
